import {regionFlowView} from '../charts/regionFlowView.js';
import {productFilter, ribbonFilter} from '../charts/regionFlows.js';

/**
 * Measures region labels in the card's own font, with a canvas: the code at weight 600 (as
 * .hc-rf-region draws it), then the count at normal weight. Null without a 2D canvas (jsdom), and
 * NaN for a font the canvas can't parse; either way the view keeps A3's estimate.
 */
function labelMeasurer(el) {
  let ctx = null;
  try {
    ctx = el.ownerDocument.createElement('canvas').getContext('2d');
  } catch (e) {
    ctx = null;
  }
  if (!ctx) {
    return null;
  }
  const width = (text, weight, fontSize, family) => {
    // A font the canvas rejects leaves ctx.font as it was: start from one that can't pass the check.
    ctx.font = '1px serif';
    ctx.font = `${weight} ${fontSize}px ${family}`;
    return ctx.font.includes(`${fontSize}px`) ? ctx.measureText(text).width : NaN;
  };
  return (region, count, fontSize) => {
    // The labels inherit the card's font-family; read each time, in case the page's styles changed.
    const family = el.ownerDocument.defaultView.getComputedStyle(el).fontFamily;
    return width(region, 600, fontSize, family) + width(count, 'normal', fontSize, family);
  };
}

/**
 * Home > Overview's "Where today's trades went" card (handoff 4.3), with its Day | Week switch:
 * <region-flow-card flows="market.flowsShown" day="market.flowDayShown" previous-day="market.previousDay"
 *                   report="market.flowReport"
 *                   span="market.flowSpan" week="market.week" loading="market.weekLoading"
 *                   on-span="showFlowSpan(span)" trading="trading">.
 * The view is worked out again only when one of those or the width changes; the template just
 * reads it. The switch asks the page (on-span), which keeps the choice and loads the week.
 * In the Day view, pressing a legend entry opens Trades filtered to that product; pressing a
 * ribbon, to its product and its seller and buyer regions. It is the board of the day drawn
 * (`report`): the previous day's while the chart shows that. Not in the Week view: Trades is one
 * day's board, and the week's colours and lanes are mostly not today's.
 */
export default function regionFlowCard($state, TradesService) {
  return {
    restrict: 'E',
    scope: {flows: '<', day: '<', previousDay: '<', report: '<', span: '<', week: '<', loading: '<', onSpan: '&', trading: '<'},
    template: require('../views/region-flow-card.html'),
    link(scope, el) {
      // The label boxes fit the labels in the page's font (regionFlowView's fitLabels).
      const measure = labelMeasurer(el[0]);
      scope.showSpan = span => scope.onSpan({span});
      // A colour opens the board of the day drawn, filtered. A press that would open
      // another tab or window is left to the link around the chart, as is any press in the Week view.
      const openFiltered = (filters, $event) => {
        if (!scope.byColour || !filters || $event.button || $event.ctrlKey || $event.metaKey || $event.shiftKey || $event.altKey) {
          return;
        }
        $event.preventDefault();
        TradesService.openWith(filters);
        $state.go('member.trades', {report: scope.report || 'current'});
      };
      scope.openProduct = (slot, $event) => openFiltered(scope.flows && productFilter(scope.flows, slot), $event);
      scope.openRibbon = (band, $event) =>
        openFiltered(scope.flows && ribbonFilter(scope.flows, band.from, band.to, band.slot), $event);
      // `width` is written by chart-width on the card body.
      scope.$watchGroup(['flows', 'day', 'previousDay', 'span', 'week', 'loading', 'trading', 'width'],
        ([flows, day, previousDay, span, week, loading, trading, width]) => {
          // The boards send members without the trading role back here, so for them the body
          // isn't a link. Always today's Trades, whichever board the member last left open, and
          // in the Week view too.
          const href = trading ? $state.href('member.trades', {report: 'current'}) : null;
          scope.byColour = !!trading && span !== 'week';
          scope.vm = regionFlowView({flows, day, previousDay, span, week, loading, width, href, measure});
        });
    }
  };
}
